<script>
  import { M } from '../../external';
  import { ignore } from '../../Common/tools';
  import { onMount } from 'svelte';
  import CommentService from './service';
  import DropdownInputWithSearchAndAdd from '../Common/DropdownInputWithSearchAndAdd.svelte';

  export let widget,
    onChange = ignore,
    refreshUI = ignore;

  let widgetData = widget.data;

  const existingContexts = {
    isLoading: false,
    failed: false,
    data: []
  };

  const numberOptions = [3, 5, 10, 20];

  const roleOptions = [
    { value: 0, name: M.ONLY_ADMINS },
    { value: 1, name: M.ADMINS_MODERATORS },
    { value: 2, name: M.ALL_MEMBERS }
  ];

  let selectedContext = {
    resourceId: '',
    name: '',
    moderation: { writePermission: 0, approvalRequired: 0 },
    extra: {
      allowMentions: false,
      allowImages: false
    }
  };

  const onChangeAndRefresh = () => {
    onChange();
    refreshUI();
  };

  const emptyContext = () => ({
    resourceId: '',
    name: '',
    moderation: { writePermission: 0, approvalRequired: 0 },
    extra: {
      allowMentions: false,
      allowImages: false
    }
  });

  const onContextChanged = (selected) => {
    if (!selected) {
      return;
    }
    widgetData.resourceId = selected.resourceId;
    // The picker is the only place the thread is chosen, so the type has to travel with the
    // id — otherwise a context created as something other than 'standalone' saves against
    // the widget's default type and its comments are never found again.
    widgetData.resourceType = selected.resourceType || widgetData.resourceType || 'standalone';
    selectedContext = selected;
    onChangeAndRefresh();
  };

  const onContextUpdated = (partialUpdate) => {
    CommentService.partialUpdateContext(selectedContext.resourceType, selectedContext.resourceId, partialUpdate);
  };

  onMount(async () => {
    existingContexts.isLoading = true;
    existingContexts.failed = false;
    // A throw here used to leave isLoading stuck true, so the thread picker never rendered
    // and the widget could never be configured — with the settings popup refusing to close
    // until it was, the only way out was reloading the builder.
    try {
      const contexts = await CommentService.getContexts();
      existingContexts.data = Array.isArray(contexts) ? contexts : [];
    } catch (e) {
      existingContexts.data = [];
      existingContexts.failed = true;
    }
    selectedContext = existingContexts.data.find((context) => context.resourceId === widgetData.resourceId) || emptyContext();
    existingContexts.isLoading = false;
  });
</script>

<div class="popup-widget-wrapper">
  <div class="input-wrapper">
    <div class="info" style="width: 100%; margin-top: 5px">
      <h5>{M.ENTER_NAME}</h5>
    </div>
    {#if !existingContexts.isLoading}
      <div class="col20 f-left">
        <DropdownInputWithSearchAndAdd
          service={CommentService}
          inputValue={selectedContext.name}
          onChange={onContextChanged}
          options={existingContexts.data} />
        {#if existingContexts.failed}
          <span class="light-gray-text">{M.ERR_UNABLE_TO_LOAD_CONTENT}</span>
        {/if}
      </div>
    {/if}
  </div>


  <div class="popup-input-wrapper mb-0 mt-10">
    <div class="info">
      <h5>{M.SOCIAL_WHO_CAN_COMMENT}</h5>
    </div>
    <select
      class="col20 f-left"
      bind:value={selectedContext.moderation.writePermission}
      on:change={() => {
        onContextUpdated({ moderation: { writePermission: selectedContext.moderation.writePermission } });
      }}>
      {#each roleOptions as option}
        <option value={option.value}>{option.name}</option>
      {/each}
    </select>
  </div>

  <div class="popup-input-wrapper mb-0">
    <div class="checkbox-wrapper">
      <input
        id="isModerationEnabled"
        checked={selectedContext?.moderation?.approvalRequired < 2}
        on:change={(event) => {
          event.target.checked
            ? onContextUpdated({ moderation: { approvalRequired: 1 } })
            : onContextUpdated({ moderation: { approvalRequired: 2 } });
        }}
        class="css-checkbox"
        type="checkbox"
        name="isModerationEnabled" />
      <label for="isModerationEnabled" class="css-label button-tooltip" data-original-title="" title=""
        >{M.SOCIAL_REQUIRE_APPROVAL}
        <p>{M.REQUIRED_APPROVAL_COMMENT_DESC}</p>
      </label>
    </div>
  </div>

  <div class="popup-input-wrapper mb-0">
    <div class="checkbox-wrapper">
      <input
        id="allowImages"
        bind:checked={selectedContext.extra.allowImages}
        on:change={() => {
          onContextUpdated({ extra: { allowImages: selectedContext.extra.allowImages } });
        }}
        class="css-checkbox"
        type="checkbox"
        name="allowImages" />
      <label for="allowImages" class="css-label button-tooltip" data-original-title="" title="">{M.ALLOW_IMAGES}</label>
    </div>
  </div>

  <div class="popup-input-wrapper mb-0">
    <div class="checkbox-wrapper">
      <input
        id="allowMentions"
        bind:checked={selectedContext.extra.allowMentions}
        on:change={() => {
          onContextUpdated({ extra: { allowMentions: selectedContext.extra.allowMentions } });
        }}
        class="css-checkbox"
        type="checkbox"
        name="allowMentions" />
      <label for="allowMentions" class="css-label button-tooltip" data-original-title="" title=""
        >{M.ALLOW_MENTIONS}</label>
    </div>
  </div>


  <div class="popup-input-wrapper mb-0 mt-10">
    <div class="info">
      <h5>{M.ORDER_FILTER}</h5>
      <span>{M.ORDER_DESC}</span>
      <select
        class="button-size-select"
        name="order"
        bind:value={widgetData.order}
        on:change={onChangeAndRefresh}
        style="width: 100%">
        <option value="asc">{M.ASCENDING}</option>
        <option value="desc">{M.DESCENDING}</option>
      </select>
    </div>
  </div>

  <div class="popup-input-wrapper mb-0 mt-10">
    <div class="info">
      <h5>{M.SOCIAL_INITIAL_NUMBER_OF_COMMENTS_TO_DISPLAY}</h5>
    </div>
    <select class="col20 f-left" bind:value={widgetData.initialCommentCount} on:change={onChangeAndRefresh}>
      {#each numberOptions as option}
        <option value={option}>{option}</option>
      {/each}
    </select>
  </div>

  <div class="popup-input-wrapper mb-0">
    <div class="checkbox-wrapper">
      <input
        id="isThreadReactionsEnabled"
        bind:checked={widgetData.isThreadReactionsEnabled}
        on:change={onChangeAndRefresh}
        class="css-checkbox"
        type="checkbox"
        name="isThreadReactionsEnabled" />
      <label for="isThreadReactionsEnabled" class="css-label button-tooltip" data-original-title="" title=""
        >{M.ENABLE_THREAD_REACTIONS}</label>
    </div>
  </div>

  <div class="popup-input-wrapper mb-0">
    <div class="checkbox-wrapper">
      <input
        id="isDarkTheme"
        bind:checked={widgetData.isDarkTheme}
        on:change={onChangeAndRefresh}
        class="css-checkbox"
        type="checkbox"
        name="isDarkTheme" />
      <label for="isDarkTheme" class="css-label button-tooltip" data-original-title="" title=""
        >{M.USE_DARK_THEME}</label>
    </div>
  </div>
</div>
